// Fuente única de cómo mostrar una emergencia médica en el FRONT: estado,
// prioridad (código) y situación. Mismo rol que `presentacionEstado` de
// luminarias: listados, filtros, gráficos, mapa y chips leen de acá.
//
// Los estados los registra el BACKEND:
//   - manuales (asignar, reasignar, cancelar, finalizar, rumbo a hospital,
//     llegó a destino): gestion-api-gestion, POST /emergencias/:id/<accion>
//   - automáticos por GPS/geocercas (en tránsito, llegó a destino, rumbo a
//     hospital, llegada a hospital): gestion-api-eventos
// Ver PLAN_EMERGENCIAS_MEDICAS.md.
import {
  EstadoEmergenciaMedica,
  IEmergencia,
  PrioridadEmergenciaMedica,
} from 'modelos/src';
import {
  IEstadoCelda,
  TonoEstado,
} from '../../../auxiliares/tabla/tabla.interfaces';

export interface IPresentacion {
  label: string;
  color: string;
  icono: string; // Material Icons
}

export const ESTADOS_EMERGENCIA: EstadoEmergenciaMedica[] = [
  'Pendiente',
  'Asignada',
  'Reasignada',
  'En tránsito',
  'Llegó a destino',
  'Rumbo a hospital',
  'Llegada a hospital',
  'Finalizada',
  'Cancelada',
];

export const ESTADOS_FINALES: EstadoEmergenciaMedica[] = [
  'Finalizada',
  'Cancelada',
];

const PRESENTACION_ESTADO: Record<EstadoEmergenciaMedica, IPresentacion> = {
  Pendiente: { label: 'Pendiente', color: '#E74C3C', icono: 'pending' },
  Asignada: { label: 'Asignada', color: '#F1C40F', icono: 'assignment_ind' },
  Reasignada: { label: 'Reasignada', color: '#3498DB', icono: 'swap_horiz' },
  'En tránsito': {
    label: 'En tránsito',
    color: '#1ABC9C',
    icono: 'local_shipping',
  },
  'Llegó a destino': {
    label: 'Llegó a destino',
    color: '#9B59B6',
    icono: 'where_to_vote',
  },
  'Rumbo a hospital': {
    label: 'Rumbo a hospital',
    color: '#D35400',
    icono: 'local_hospital',
  },
  'Llegada a hospital': {
    label: 'Llegada a hospital',
    color: '#E67E22',
    icono: 'domain',
  },
  Finalizada: { label: 'Finalizada', color: '#2ECC71', icono: 'check_circle' },
  Cancelada: { label: 'Cancelada', color: '#C0392B', icono: 'cancel' },
};

const SIN_ESTADO: IPresentacion = {
  label: 'Sin estado',
  color: '#9e9e9e',
  icono: 'question_mark',
};

export function presentacionEstado(estado?: string | null): IPresentacion {
  return PRESENTACION_ESTADO[estado as EstadoEmergenciaMedica] ?? SIN_ESTADO;
}

export function estadoDe(e?: IEmergencia | null): EstadoEmergenciaMedica | '' {
  return (e?.ultimoEventoEmergencia?.estado as EstadoEmergenciaMedica) ?? '';
}

export function estaFinalizada(e?: IEmergencia | null): boolean {
  return ESTADOS_FINALES.includes(estadoDe(e) as EstadoEmergenciaMedica);
}

// Prioridad (código de triage)

export const PRIORIDADES: PrioridadEmergenciaMedica[] = [
  'Rojo',
  'Amarillo',
  'Verde',
  'Celeste',
  'Negro',
];

const PRESENTACION_PRIORIDAD: Record<PrioridadEmergenciaMedica, IPresentacion> =
  {
    Rojo: { label: 'Rojo', color: '#e53935', icono: 'emergency' },
    Amarillo: { label: 'Amarillo', color: '#fbc02d', icono: 'warning' },
    Verde: { label: 'Verde', color: '#388e3c', icono: 'healing' },
    Celeste: { label: 'Celeste', color: '#00bcd4', icono: 'support_agent' },
    // Negro puro se pierde sobre el fondo oscuro
    Negro: { label: 'Negro', color: '#424242', icono: 'dark_mode' },
  };

export function presentacionPrioridad(
  prioridad?: string | null,
): IPresentacion {
  return (
    PRESENTACION_PRIORIDAD[prioridad as PrioridadEmergenciaMedica] ?? {
      label: 'Sin código',
      color: '#9e9e9e',
      icono: 'question_mark',
    }
  );
}

// Situación con la que se registró

export type SituacionEmergencia =
  | 'Auxilio'
  | 'Seguimiento'
  | 'Registrada finalizada'
  | 'Llamada'
  | 'Importada';

export function situacionDe(e?: IEmergencia | null): SituacionEmergencia {
  if (e?.importada) return 'Importada';
  if (e?.esAuxilio) return 'Auxilio';
  if (e?.enSeguimiento) return 'Seguimiento';
  if (e?.iniciaFinalizada) return 'Registrada finalizada';
  return 'Llamada';
}

const PRESENTACION_SITUACION: Record<SituacionEmergencia, IPresentacion> = {
  Auxilio: { label: 'Auxilio', color: '#e53935', icono: 'emergency' },
  Seguimiento: {
    label: 'En seguimiento',
    color: '#3498DB',
    icono: 'monitor_heart',
  },
  'Registrada finalizada': {
    label: 'Registrada finalizada',
    color: '#2ECC71',
    icono: 'task_alt',
  },
  Llamada: { label: 'Llamada', color: '#7e57c2', icono: 'call' },
  Importada: { label: 'Importada', color: '#78909c', icono: 'upload_file' },
};

export function presentacionSituacion(s: SituacionEmergencia): IPresentacion {
  return PRESENTACION_SITUACION[s];
}

/**
 * Badge redondeado para celdas `html:` de app-tabla (mismo estilo que
 * luminarias). El texto se mezcla con --badge-contraste (negro en claro,
 * blanco en oscuro) para que colores claros como el amarillo se lean.
 */
export function htmlBadge(p: IPresentacion): string {
  return `<span style="display:inline-flex;align-items:center;gap:3px;padding:1px 8px;border-radius:10px;background:color-mix(in srgb, ${p.color} 16%, transparent);color:color-mix(in srgb, ${p.color} 72%, var(--badge-contraste, #000));font-size:11px;font-weight:600;width:fit-content;white-space:nowrap;"><span class="material-icons" style="font-size:13px;">${p.icono}</span>${p.label}</span>`;
}

// Celdas de listados (pieza `estado` de app-tabla o `estadoHtml`)
//
// Los colores de arriba siguen sirviendo para el mapa, los gráficos y los
// detalles. En los listados cada valor va con el tono de la tabla común según
// lo que significa (docs/GUIA_ESTETICA_LISTADOS.md), nunca con un color propio.

const TONO_ESTADO: Record<EstadoEmergenciaMedica, TonoEstado> = {
  // Sin móvil todavía: hay que atenderla
  Pendiente: 'aviso',
  Asignada: 'info',
  Reasignada: 'info',
  'En tránsito': 'info',
  'Llegó a destino': 'info',
  'Rumbo a hospital': 'info',
  'Llegada a hospital': 'info',
  Finalizada: 'ok',
  Cancelada: 'neutro',
};

// Íconos de docs/ICONOS.md donde el concepto está en la guía
const ICONO_ESTADO_CELDA: Partial<Record<EstadoEmergenciaMedica, string>> = {
  'En tránsito': 'airport_shuttle',
  Finalizada: 'task_alt',
  Cancelada: 'block',
};

/** Estado de la emergencia para un listado. */
export function estadoCeldaEmergencia(estado?: string | null): IEstadoCelda {
  const p = presentacionEstado(estado);
  const e = estado as EstadoEmergenciaMedica;
  return {
    texto: p.label,
    tono: TONO_ESTADO[e] ?? 'neutro',
    icono: ICONO_ESTADO_CELDA[e] ?? p.icono,
  };
}

const TONO_PRIORIDAD: Record<PrioridadEmergenciaMedica, TonoEstado> = {
  Rojo: 'error',
  Amarillo: 'aviso',
  Verde: 'ok',
  Celeste: 'info',
  Negro: 'neutro',
};

/**
 * Código (de apertura o de cierre) para un listado: el nombre del color va
 * escrito y el tono lo acompaña. Sin ícono: el código no es un estado.
 */
export function prioridadCelda(prioridad?: string | null): IEstadoCelda {
  return {
    texto: presentacionPrioridad(prioridad).label,
    tono: TONO_PRIORIDAD[prioridad as PrioridadEmergenciaMedica] ?? 'neutro',
  };
}

const TONO_SITUACION: Record<SituacionEmergencia, TonoEstado> = {
  Auxilio: 'error',
  Seguimiento: 'info',
  'Registrada finalizada': 'ok',
  Llamada: 'neutro',
  Importada: 'neutro',
};

/** Situación con la que se registró, para un listado. */
export function situacionCelda(s: SituacionEmergencia): IEstadoCelda {
  const p = presentacionSituacion(s);
  return { texto: p.label, tono: TONO_SITUACION[s], icono: p.icono };
}

/** Color de un tono para filtros (punto o ícono de las opciones). */
export function colorTono(tono: TonoEstado): string {
  const color: Record<TonoEstado, string> = {
    ok: 'green',
    aviso: 'orange',
    error: 'red',
    info: 'blue',
    neutro: 'gray',
  };
  return `var(--color-${color[tono]}-text)`;
}
